import { SchedulerPackagingRoutesRowType } from './Scheduler.types'
import { joinPeriods, parsePeriods } from './RulesWorkbench.timeline'

/**
 * "Specific Time" on the demand requirements table: a product that may only
 * be packed inside the windows the planner marks. The engine enforces it
 * (`product_allowed_windows`); this file is the Portal half, the check made
 * when the windows are saved that they are long enough to make the demand.
 */

/** The service stores booleans as text, and 'False' is truthy. */
export const asBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** PR as the engine reads it: blank is 100%, anything over 1 a percentage. */
const prFactor = (v: unknown): number => {
  if (v === null || v === undefined || String(v).trim() === '') return 1
  const n = Number(String(v).replace('%', ''))
  if (!Number.isFinite(n) || n <= 0) return 1
  return n > 1 ? n / 100 : n
}

/** What a row's Specific Time windows mean (`specificTimeMode`). */
export type SpecificTimeMode = 'within' | 'start'

export const specificTimeModeOf = (row: {
  specificTimeMode?: string | null
}): SpecificTimeMode =>
  String(row.specificTimeMode ?? '')
    .trim()
    .toLowerCase() === 'start'
    ? 'start'
    : 'within'

export const SPECIFIC_TIME_MODE_LABEL: Record<SpecificTimeMode, string> = {
  within: 'Start and finish within',
  start: 'Start within',
}

/**
 * A window rule as typed in an uploaded CSV, or `null` for a blank cell.
 * Case, spaces and punctuation are ignored, so the downloaded labels ("Start
 * within", "Start and finish within") read back, and so do the shorter forms
 * a planner types: "start", "startandfinish", "start & finish", "within".
 * Anything that is not "start" alone (optionally "start within") is the
 * default, start and finish within — the reading every row had before the
 * rule existed.
 */
export const specificTimeModeFromText = (
  v: unknown
): SpecificTimeMode | null => {
  const s = String(v ?? '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')
  if (s === '' || s === 'null') return null
  return s === 'start' || s === 'startwithin' || s === 'startin'
    ? 'start'
    : 'within'
}

const isBlankCell = (v: unknown): boolean => {
  const s = String(v ?? '').trim()
  return s === '' || s.toLowerCase() === 'null'
}

/**
 * The Specific Time cells of one uploaded row, put into the form the popup
 * saves: windows as `(start,end),…`, the rule as `within` / `start`, the tick
 * as `True` / `False`. Only the columns the file actually carries are
 * touched — an upload merges on the row key, so writing a column the file
 * did not have would overwrite what the table already holds.
 *
 * The tick follows the popup's rule: ticked with no window is saved unticked
 * (`saveSpecificTime`). A file that gives windows but leaves the tick blank
 * means them, so a blank tick beside windows reads as ticked; an explicit
 * FALSE beside windows keeps them but stays unticked.
 */
export const normaliseSpecificTimeUploadRow = (
  row: Record<string, unknown>
): Record<string, unknown> => {
  const hasTick = 'specificTime' in row
  const hasWindows = 'specificTimeWindows' in row
  const hasMode = 'specificTimeMode' in row
  if (!hasTick && !hasWindows && !hasMode) return row
  const out = { ...row }
  if (hasWindows) {
    out.specificTimeWindows = joinPeriods(
      parsePeriods(String(row.specificTimeWindows ?? '')).filter(
        ([s, e]) => e > s
      )
    )
  }
  if (hasMode) out.specificTimeMode = specificTimeModeFromText(row.specificTimeMode) ?? ''
  if (hasTick || hasWindows) {
    const windowsGiven = hasWindows && out.specificTimeWindows !== ''
    const ticked = hasWindows
      ? windowsGiven &&
        (!hasTick || isBlankCell(row.specificTime) || asBool(row.specificTime))
      : asBool(row.specificTime)
    out.specificTime = ticked ? 'True' : 'False'
    // A ticked row whose rule cell is blank gets the default written down,
    // so the stored row says what it means. Never when the file has no rule
    // column: that would overwrite a "Start within" the table already holds.
    if (ticked && hasMode && !out.specificTimeMode) out.specificTimeMode = 'within'
  }
  return out
}

/**
 * Download half: the Specific Time windows and rule as two columns beside the
 * tick, in the format the upload reads back. Written whether or not the row is
 * ticked: unticking keeps the windows (re-ticking starts from them), so a
 * download-then-upload must carry them back rather than blank them. The tick
 * column says whether they apply.
 */
export const specificTimeDownloadColumns = (
  row: Record<string, unknown>
): { specificTimeWindows: string; specificTimeMode: string } => {
  const raw = String(row.specificTimeWindows ?? '').trim()
  const windows = raw.toLowerCase() === 'null' ? '' : raw
  return {
    specificTimeWindows: windows,
    specificTimeMode: windows
      ? SPECIFIC_TIME_MODE_LABEL[specificTimeModeOf(row)]
      : '',
  }
}

/** For a "Start within" row: the hours from the first window's start to the
 *  end of the week, which is all the room a run started there can use. */
export const startWindowHours = (value: string | null | undefined): number => {
  const starts = parsePeriods(value)
    .filter(([s, e]) => e > s)
    .map(([s]) => s)
  return starts.length ? Math.max(0, 168 - Math.min(...starts)) : 0
}

/** Total hours covered by the windows, counting an overlap once. */
export const windowHours = (value: string | null | undefined): number => {
  const spans = parsePeriods(value)
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0])
  let total = 0
  let reach = -Infinity
  for (const [s, e] of spans) {
    const from = Math.max(s, reach)
    if (e > from) total += e - from
    reach = Math.max(reach, e)
  }
  return total
}

/**
 * Fastest rate, in product units per hour, at which each product can be
 * packed on one line: the line's run rate times its PR, capped by what the
 * route's kitchen can supply (`kitchenRunRate / conversionFactor`, the same
 * cap the decoder applies). Keyed by productCode; the conversion factor comes
 * from the demand row, so it is applied later in `hoursNeeded`.
 */
export type ProductRates = Record<
  string,
  { line: string; lineRate: number; kitchenRate: number }[]
>

export const productRatesFromRoutes = (
  routes: SchedulerPackagingRoutesRowType[]
): ProductRates => {
  const out: ProductRates = {}
  for (const r of routes) {
    if (!r?.productCode) continue
    const row = r as unknown as Record<string, unknown>
    const kitchenRate = num(r.kitchenRunRate)
    for (const i of [1, 2, 3, 4]) {
      const line = row[`packingLine${i}`]
      const rate = num(row[`lineRunRate${i}`])
      if (!line || rate <= 0) continue
      ;(out[String(r.productCode)] ??= []).push({
        line: String(line),
        lineRate: rate * prFactor(row[`pr${i}`]),
        kitchenRate,
      })
    }
  }
  return out
}

/** Hours to make `quantity` on the product's fastest line, or null when no
 *  route gives it a rate (nothing to check against). */
export const hoursNeeded = (
  rates: ProductRates,
  productCode: string,
  quantity: number,
  conversionFactor: number
): { hours: number; line: string; rate: number } | null => {
  const conv = conversionFactor > 0 ? conversionFactor : 1
  let best: { line: string; rate: number } | null = null
  for (const r of rates[productCode] ?? []) {
    const rate =
      r.kitchenRate > 0
        ? Math.min(r.lineRate, r.kitchenRate / conv)
        : r.lineRate
    if (rate > 0 && (!best || rate > best.rate)) best = { line: r.line, rate }
  }
  if (!best || quantity <= 0) return null
  return {
    hours: Math.ceil(quantity / best.rate),
    line: best.line,
    rate: best.rate,
  }
}

/** The warning shown before saving windows too short for the demand, or null. */
export const shortWindowWarning = (
  rates: ProductRates,
  row: {
    productCode: string
    productName?: string
    quantity: unknown
    quantityStillToMake?: unknown
    conversionFactor?: unknown
  },
  windows: string,
  mode: SpecificTimeMode = 'within'
): string | null => {
  // A stated remainder is what the engine plans against, as it does here.
  const still = num(row.quantityStillToMake)
  const quantity = still > 0 ? still : num(row.quantity)
  const need = hoursNeeded(
    rates,
    String(row.productCode),
    quantity,
    num(row.conversionFactor)
  )
  if (!need) return null
  // "Start within": a run may carry on past its window, so the room is from
  // the window's start to the end of the week (the engine estimates the same).
  const allowed =
    mode === 'start' ? startWindowHours(windows) : windowHours(windows)
  if (allowed >= need.hours) return null
  const name = row.productName ? ` (${row.productName})` : ''
  if (mode === 'start') {
    return (
      `There is not enough of the week left after the start window for ` +
      `${row.productCode}${name}. Making ${quantity.toLocaleString()} on its ` +
      `fastest line, ${need.line} at ${Math.round(need.rate).toLocaleString()} ` +
      `an hour, needs ${need.hours}h, but starting at the earliest window ` +
      `leaves only ${allowed}h in the week. ` +
      `Whatever does not fit will be left as unmet demand.`
    )
  }
  return (
    `The time windows for ${row.productCode}${name} are too short. ` +
    `Making ${quantity.toLocaleString()} on its fastest line, ${need.line} ` +
    `at ${Math.round(need.rate).toLocaleString()} an hour, needs ${need.hours}h, ` +
    `but the windows allow only ${allowed}h. ` +
    `Whatever does not fit will be left as unmet demand.`
  )
}

/** A value typed in an hours column, or null when the cell is blank. */
const hourOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || String(v).trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export type TimeWindowShortfall = {
  quantity: number
  from: number
  to: number
  allowedHours: number
  neededHours: number
  line: string
  rate: number
  /** What the run keeps: the engine trims demand to this and reports the
   *  rest as unmet (`preprocess::trim_demand_to_time_window`). */
  keep: number
}

/**
 * Don't Start Before / Finish Before too close together for the demand on the
 * product's fastest line, or null. A blank Don't Start Before is hour 0, as
 * the engine reads it, so only a row with a Finish Before is judged. Specific
 * Time rows are left to their own windows check. The engine makes the same
 * estimate and trims the demand to `keep`, so this is what the planner is
 * agreeing to when they save anyway.
 */
export const timeWindowShortfall = (
  rates: ProductRates,
  row: {
    productCode: string
    quantity: unknown
    quantityStillToMake?: unknown
    conversionFactor?: unknown
    dontStartBefore?: unknown
    finishBefore?: unknown
    specificTime?: unknown
  }
): TimeWindowShortfall | null => {
  const to = hourOrNull(row.finishBefore)
  if (to === null || asBool(row.specificTime)) return null
  const from = hourOrNull(row.dontStartBefore) ?? 0
  const allowedHours = to - from
  if (allowedHours <= 0) return null
  const still = num(row.quantityStillToMake)
  const quantity = still > 0 ? still : num(row.quantity)
  const need = hoursNeeded(
    rates,
    String(row.productCode),
    quantity,
    num(row.conversionFactor)
  )
  if (!need || need.hours <= allowedHours) return null
  return {
    quantity,
    from,
    to,
    allowedHours,
    neededHours: need.hours,
    line: need.line,
    rate: need.rate,
    keep: Math.floor(allowedHours * need.rate),
  }
}

/**
 * The two "not enough time to make the demand" checks the table makes when a
 * row is edited, made over an uploaded file instead. An upload used to skip
 * both, so a window too short for its demand went in silently and showed up
 * only as unmet demand after the next run (31690387021: 901754201, 4,400 units
 * needing about 29h, given a 23h "start and finish within" window).
 *
 * Each uploaded row is read on top of the table's current row for the same
 * product, because a file need not carry every column: a quantity-only upload
 * can make existing windows too short, and a windows-only upload is judged
 * against the quantity already in the table. Returns one sentence per short
 * row, in file order; empty when everything fits.
 */
export const uploadShortfallWarnings = (
  uploaded: Record<string, unknown>[],
  existing: Record<string, unknown>[],
  rates: ProductRates
): string[] => {
  const current = new Map<string, Record<string, unknown>>()
  for (const r of existing) {
    const code = String(r?.productCode ?? '').trim()
    if (code) current.set(code, r)
  }
  const out: string[] = []
  for (const up of uploaded) {
    const code = String(up?.productCode ?? '').trim()
    if (!code) continue
    const row: Record<string, unknown> = {
      ...(current.get(code) ?? {}),
      ...up,
      productCode: code,
    }
    const label = row.productName ? `${code} ${String(row.productName)}` : code
    const windows = String(row.specificTimeWindows ?? '')
    if (asBool(row.specificTime) && parsePeriods(windows).length > 0) {
      const msg = shortWindowWarning(
        rates,
        row as Parameters<typeof shortWindowWarning>[1],
        windows,
        specificTimeModeOf(row as { specificTimeMode?: string | null })
      )
      if (msg) out.push(msg)
      continue
    }
    const gap = timeWindowShortfall(
      rates,
      row as Parameters<typeof timeWindowShortfall>[1]
    )
    if (gap)
      out.push(
        `${label}: needs ${gap.neededHours}h to make ${gap.quantity.toLocaleString()} ` +
          `on ${gap.line}, but Don't Start Before ${gap.from}h to Finish Before ` +
          `${gap.to}h allows ${gap.allowedHours}h. The run will keep ` +
          `${gap.keep.toLocaleString()} and report the other ` +
          `${Math.max(0, gap.quantity - gap.keep).toLocaleString()} as unmet demand.`
      )
  }
  return out
}
